import { StreamingService } from './streamingService.js';
import { SessionService } from './sessionService.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import type { Message, MentionedContact } from '@/types/message.js';
import type { Contact } from '@/types/contact.js';
import { useChatStore } from '@/stores/chatStore.js';
import { getApiUrl } from '@/lib/api-url.js';

export class ChatService {
    private baseUrl: string;

    constructor(
        private streamingService: StreamingService,
        private sessionService: SessionService,
        private getToken: () => string | null
    ) {
        this.baseUrl = getApiUrl();
    }

    async sendMessage(
        content: string,
        imageData?: { base64: string; mimeType: string },
        fileData?: { base64: string; mimeType: string; filename?: string },
        mentionedContactsParam?: Contact[]
    ): Promise<void> {
        const state = useSessionStore.getState();
        let sessionId = state.currentSessionId;

        console.log('[ChatService] sendMessage - Initial State:', {
            isWelcomeState: state.isWelcomeState,
            currentSessionId: state.currentSessionId,
            isCreatingSession: state.isCreatingSession,
            content: content.slice(0, 50) + '...'
        });

        // IMPORTANT: Always create NEW session if in welcome state
        // This ensures "+ New Chat" button works correctly
        if (state.isWelcomeState) {
            if (state.isCreatingSession) {
                return; // Already creating a session
            }

            state.setCreatingSession(true);
            try {
                // Create brand new session
                console.log('[ChatService] Creating NEW session...');
                sessionId = await this.sessionService.createSession();
                console.log('[ChatService] New session created:', sessionId);

                state.setCurrentSession(sessionId);
                state.setWelcomeState(false);

                // Broadcast session creation event so UI can update
                state.notifySessionEvent({
                    type: 'created',
                    sessionId: sessionId,
                });

                // Convert the active welcome tab to a session tab
                const { tabs, activeTabId, addTab, removeTab } = useChatTabsStore.getState();
                const activeTab = tabs.find((t) => t.id === activeTabId);

                console.log('[ChatService] Converting welcome tab to session tab:', {
                    activeTabId,
                    activeTab,
                    sessionId
                });

                if (activeTab && activeTab.sessionId === null) {
                    // This is a welcome tab, convert it to a session tab
                    const newTabId = `session-${sessionId}`;
                    const oldTabId = activeTab.id;

                    // Create new session tab (will automatically activate it)
                    addTab({
                        id: newTabId,
                        title: activeTab.title,
                        sessionId: sessionId,
                        isPinned: false,
                    });

                    // Remove old welcome tab
                    removeTab(oldTabId);

                    console.log('[ChatService] Welcome tab converted to session tab:', newTabId);
                }

                console.log('[ChatService] Session state updated:', {
                    newSessionId: sessionId,
                    isWelcomeState: useSessionStore.getState().isWelcomeState,
                    currentSessionId: useSessionStore.getState().currentSessionId
                });
            } catch (error) {
                console.error('Failed to create session:', error);
                throw error;
            } finally {
                state.setCreatingSession(false);
            }
        }

        if (!sessionId) {
            throw new Error('No session available');
        }

        // Use mentioned contacts passed from ChatInput
        const mentionedContacts: MentionedContact[] = [];
        let enrichedContent = content;

        console.log('[ChatService] Received contacts param:', mentionedContactsParam);

        if (mentionedContactsParam && mentionedContactsParam.length > 0) {
            for (const contact of mentionedContactsParam) {
                mentionedContacts.push({
                    id: contact.id,
                    name: contact.agentCard.name,
                    agentCard: contact.agentCard,
                });
            }

            // Inject agent card context into message content
            const contactContext = mentionedContactsParam
                .map((contact) => {
                    const userId = contact.agentCard.metadata?.dexto?.owner?.userId;
                    const username = contact.agentCard.metadata?.dexto?.owner?.username;

                    // For internal contacts (with userId), only show userId to force delegate_to_agent
                    // For external contacts (no userId), show URL for delegate_to_url
                    let info = `\n\n---\nAgent Contact: ${contact.agentCard.name}`;

                    if (userId) {
                        // Internal contact - only show User ID
                        info += `\nUser ID: ${userId}`;
                        if (username) {
                            info += `\nUsername: ${username}`;
                        }
                        info += `\nDescription: ${contact.agentCard.description}`;
                        info += `\n[Internal contact - use delegate_to_agent tool with this User ID]`;
                    } else {
                        // External contact - show URL
                        info += `\nURL: ${contact.agentCard.url}`;
                        info += `\nDescription: ${contact.agentCard.description}`;
                        info += `\n[External contact - use delegate_to_url tool with this URL]`;
                    }

                    info += `\n---`;
                    return info;
                })
                .join('');
            enrichedContent = `${content}${contactContext}`;

            console.log('[ChatService] Enriched content:', enrichedContent);
        }

        // Add user message to UI immediately (with original content, not enriched)
        const userMessage: Message = {
            id: `msg-${Date.now()}-user`,
            role: 'user',
            content,
            createdAt: Date.now(),
            sessionId,
            ...(mentionedContacts.length > 0 && { mentionedContacts }),
            ...(imageData && { image: imageData }),
            ...(fileData && { file: fileData }),
        };
        useChatStore.getState().addMessage(userMessage);

        // Send via SSE streaming endpoint with multimodal support
        await this.streamingService.sendMessageStream(enrichedContent, sessionId, imageData, fileData);
    }

    async cancel(sessionId?: string): Promise<void> {
        const currentSessionId = sessionId || useSessionStore.getState().currentSessionId;
        if (!currentSessionId) {
            return;
        }

        const token = this.getToken();
        if (!token) {
            console.warn('[ChatService] No token available for cancel');
            return;
        }

        // Cancel via REST API
        try {
            const response = await fetch(`${this.baseUrl}/sessions/${currentSessionId}/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                console.warn('[ChatService] Cancel request failed:', response.status);
            }
        } catch (error) {
            console.error('[ChatService] Cancel error:', error);
        }

        // Also cancel the SSE stream
        this.streamingService.cancelStream();

        useChatStore.getState().setProcessing(currentSessionId, false);
    }

    async reset(sessionId?: string): Promise<void> {
        const currentSessionId = sessionId || useSessionStore.getState().currentSessionId;
        if (!currentSessionId) {
            return;
        }

        const token = this.getToken();
        if (!token) {
            console.warn('[ChatService] No token available for reset');
            return;
        }

        // Reset via REST API
        try {
            const response = await fetch(`${this.baseUrl}/reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ sessionId: currentSessionId }),
            });

            if (!response.ok) {
                console.warn('[ChatService] Reset request failed:', response.status);
            }
        } catch (error) {
            console.error('[ChatService] Reset error:', error);
        }
    }
}
